// Demo data generator for preview/demo mode
// Provides realistic sample data for non-authenticated users

export const demoDataGenerator = {
  // Treatment Analytics Demo Data
  getTreatmentAnalytics: () => ({
    treatments: [
      {
        id: 'demo-1',
        treatmentName: 'Spider Mite Control - Week 1',
        product: 'Phytoseiulus persimilis',
        field: 'Greenhouse A - Zone 1',
        applicationDate: '2024-01-15',
        effectiveness: 94.5,
        cost: 245.00,
        pestPressure: 'High',
        cropType: 'Cannabis',
        applicationMethod: 'Beneficial Release'
      },
      {
        id: 'demo-2',
        treatmentName: 'Powdery Mildew Prevention',
        product: 'Neem Oil Spray',
        field: 'Greenhouse B - Zone 2',
        applicationDate: '2024-01-18',
        effectiveness: 87.2,
        cost: 89.50,
        pestPressure: 'Medium',
        cropType: 'Tomatoes',
        applicationMethod: 'Foliar Spray'
      },
      {
        id: 'demo-3',
        treatmentName: 'Aphid Management',
        product: 'Aphidius colemani',
        field: 'Greenhouse A - Zone 3',
        applicationDate: '2024-01-20',
        effectiveness: 91.8,
        cost: 178.00,
        pestPressure: 'Medium',
        cropType: 'Peppers',
        applicationMethod: 'Beneficial Release'
      },
      {
        id: 'demo-4',
        treatmentName: 'Thrips Control Program',
        product: 'Amblyseius cucumeris',
        field: 'Greenhouse C - Zone 1',
        applicationDate: '2024-01-22',
        effectiveness: 88.5,
        cost: 312.00,
        pestPressure: 'High',
        cropType: 'Cucumbers',
        applicationMethod: 'Beneficial Release'
      },
      {
        id: 'demo-5',
        treatmentName: 'Fungus Gnat Suppression',
        product: 'Steinernema feltiae',
        field: 'Greenhouse B - Zone 1',
        applicationDate: '2024-01-25',
        effectiveness: 93.2,
        cost: 156.00,
        pestPressure: 'Low',
        cropType: 'Lettuce',
        applicationMethod: 'Soil Drench'
      }
    ],
    metrics: {
      totalTreatments: 47,
      avgEffectiveness: 91.2,
      topProduct: 'Phytoseiulus persimilis',
      mostTreatedField: 'Greenhouse A',
      totalCost: 12450.00,
      successRate: 89.4
    },
    fields: [
      { id: 'field-1', name: 'Greenhouse A', treatments: 18, avgEffectiveness: 92.1 },
      { id: 'field-2', name: 'Greenhouse B', treatments: 15, avgEffectiveness: 88.7 },
      { id: 'field-3', name: 'Greenhouse C', treatments: 14, avgEffectiveness: 93.5 }
    ]
  }),

  // Team Performance Demo Data
  getTeamPerformance: () => ({
    metrics: [
      {
        title: 'Task Completion Rate',
        value: 92.5,
        unit: '%',
        change: 5.2,
        target: 95,
        icon: 'CheckCircle',
        color: 'success'
      },
      {
        title: 'Average Task Time',
        value: 2.3,
        unit: 'hours',
        change: -8.1,
        target: 2.0,
        icon: 'Clock',
        color: 'warning'
      },
      {
        title: 'Team Efficiency',
        value: 87.8,
        unit: '%',
        change: 3.4,
        target: 90,
        icon: 'TrendingUp',
        color: 'success'
      },
      {
        title: 'Active Tasks',
        value: 23,
        unit: 'tasks',
        change: -5,
        target: 20,
        icon: 'ListTodo',
        color: 'info'
      }
    ],
    teamMembers: [
      {
        id: 'member-1',
        name: 'Sarah Johnson',
        role: 'IPM Specialist',
        tasksCompleted: 34,
        efficiency: 94.2,
        avatar: null
      },
      {
        id: 'member-2',
        name: 'Mike Chen',
        role: 'Field Manager',
        tasksCompleted: 28,
        efficiency: 91.5,
        avatar: null
      },
      {
        id: 'member-3',
        name: 'Emily Rodriguez',
        role: 'Compliance Officer',
        tasksCompleted: 31,
        efficiency: 88.7,
        avatar: null
      },
      {
        id: 'member-4',
        name: 'David Park',
        role: 'Treatment Technician',
        tasksCompleted: 26,
        efficiency: 85.3,
        avatar: null
      }
    ],
    tasks: [
      {
        id: 'task-1',
        title: 'Spider Mite Treatment - Greenhouse A',
        assignee: 'Sarah Johnson',
        status: 'completed',
        priority: 'high',
        dueDate: '2024-01-15',
        completedDate: '2024-01-15'
      },
      {
        id: 'task-2',
        title: 'Weekly Pest Scouting - All Zones',
        assignee: 'Mike Chen',
        status: 'in_progress',
        priority: 'medium',
        dueDate: '2024-01-18',
        completedDate: null
      },
      {
        id: 'task-3',
        title: 'Compliance Report Submission',
        assignee: 'Emily Rodriguez',
        status: 'pending',
        priority: 'high',
        dueDate: '2024-01-20',
        completedDate: null
      }
    ]
  }),

  // Calculator Demo Data
  getCalculatorExamples: () => ({
    chemical: {
      productName: 'Neem Oil Concentrate',
      concentration: 70,
      targetRate: 2.5,
      areaSize: 1000,
      unit: 'sq ft',
      result: {
        productNeeded: 35.7,
        waterNeeded: 2.5,
        mixingRatio: '1:70',
        applicationVolume: 2.535
      }
    },
    beneficial: {
      pest: 'Spider Mites',
      beneficial: 'Phytoseiulus persimilis',
      pestPressure: 'high',
      areaSize: 500,
      cropType: 'Cannabis',
      result: {
        releaseRate: 10,
        totalBeneficials: 5000,
        packagesNeeded: 5,
        releaseSchedule: 'Weekly for 3 weeks',
        estimatedCost: 245.00
      }
    },
    tankMixing: {
      products: [
        { name: 'Neem Oil', amount: 32, unit: 'oz' },
        { name: 'Insecticidal Soap', amount: 16, unit: 'oz' }
      ],
      tankSize: 5,
      compatibility: 'Compatible',
      mixingOrder: ['Water (50%)', 'Neem Oil', 'Insecticidal Soap', 'Water (remaining)']
    }
  }),

  // Notifications Demo Data
  getNotifications: () => ({
    notifications: [
      {
        id: 'notif-1',
        type: 'compliance',
        severity: 'critical',
        title: 'Pesticide Application Record Overdue',
        message: 'Application record for Greenhouse A (01/15/2024) requires immediate documentation',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000)?.toISOString(),
        isAcknowledged: false,
        actionRequired: true
      },
      {
        id: 'notif-2',
        type: 'environmental',
        severity: 'high',
        title: 'Temperature Alert - Greenhouse B',
        message: 'Temperature exceeded optimal range (82°F). Beneficial efficacy may be reduced.',
        timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000)?.toISOString(),
        isAcknowledged: false,
        actionRequired: false
      },
      {
        id: 'notif-3',
        type: 'treatment',
        severity: 'medium',
        title: 'Scheduled Treatment Reminder',
        message: 'Spider mite follow-up treatment due in Greenhouse A - Zone 1',
        timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000)?.toISOString(),
        isAcknowledged: true,
        actionRequired: true
      },
      {
        id: 'notif-4',
        type: 'audit',
        severity: 'low',
        title: 'Weekly Audit Report Generated',
        message: 'Your weekly compliance audit report is ready for review',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString(),
        isAcknowledged: true,
        actionRequired: false
      },
      {
        id: 'notif-5',
        type: 'team',
        severity: 'medium',
        title: 'Task Assignment Update',
        message: 'New task assigned: Pest scouting for Greenhouse C',
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000)?.toISOString(),
        isAcknowledged: false,
        actionRequired: true
      }
    ],
    stats: {
      total: 47,
      unacknowledged: 12,
      critical: 3,
      high: 8,
      medium: 15,
      low: 21
    },
    trends: [
      { date: '2024-01-15', count: 8 },
      { date: '2024-01-16', count: 12 },
      { date: '2024-01-17', count: 6 },
      { date: '2024-01-18', count: 15 },
      { date: '2024-01-19', count: 9 },
      { date: '2024-01-20', count: 11 },
      { date: '2024-01-21', count: 7 }
    ]
  }),

  // Audit Log Demo Data
  getAuditLogs: () => ({
    logs: [
      {
        id: 'audit-1',
        eventType: 'treatment_applied',
        action: 'Applied beneficial insects',
        user: 'Sarah Johnson',
        userId: 'user-1',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000)?.toISOString(),
        severity: 'info',
        details: {
          product: 'Phytoseiulus persimilis',
          location: 'Greenhouse A - Zone 1',
          quantity: '1000 units'
        },
        ipAddress: '192.168.1.45',
        device: 'Mobile - iOS'
      },
      {
        id: 'audit-2',
        eventType: 'compliance_violation',
        action: 'Documentation delay detected',
        user: 'System',
        userId: 'system',
        timestamp: new Date(Date.now() - 5 * 60 * 60 * 1000)?.toISOString(),
        severity: 'warning',
        details: {
          violation: 'Application record not submitted within 24 hours',
          location: 'Greenhouse B',
          daysOverdue: 2
        },
        ipAddress: 'N/A',
        device: 'Server'
      },
      {
        id: 'audit-3',
        eventType: 'user_login',
        action: 'User logged in',
        user: 'Mike Chen',
        userId: 'user-2',
        timestamp: new Date(Date.now() - 8 * 60 * 60 * 1000)?.toISOString(),
        severity: 'info',
        details: {
          loginMethod: 'Email/Password',
          location: 'Portland, OR'
        },
        ipAddress: '192.168.1.67',
        device: 'Desktop - Chrome'
      },
      {
        id: 'audit-4',
        eventType: 'data_export',
        action: 'Exported treatment report',
        user: 'Emily Rodriguez',
        userId: 'user-3',
        timestamp: new Date(Date.now() - 12 * 60 * 60 * 1000)?.toISOString(),
        severity: 'info',
        details: {
          reportType: 'Monthly Compliance Report',
          format: 'PDF',
          dateRange: 'December 2023'
        },
        ipAddress: '192.168.1.89',
        device: 'Desktop - Firefox'
      },
      {
        id: 'audit-5',
        eventType: 'settings_changed',
        action: 'Updated notification preferences',
        user: 'David Park',
        userId: 'user-4',
        timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000)?.toISOString(),
        severity: 'info',
        details: {
          changes: 'Enabled SMS alerts for critical notifications',
          previousValue: 'Email only',
          newValue: 'Email + SMS'
        },
        ipAddress: '192.168.1.102',
        device: 'Mobile - Android'
      }
    ],
    stats: {
      totalEvents: 1247,
      todayEvents: 34,
      criticalEvents: 5,
      warningEvents: 18,
      infoEvents: 1224,
      uniqueUsers: 12
    }
  })
};

export default demoDataGenerator;